// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-COST-6 — the Profitability "v2 (beta)" tab. Net profit = revenue − reserve − every expense (Azure +
// GitHub + other costs), then Blue Dev / Owl. Azure comes from the daily cost export (v2.3) or the hourly
// snapshot; never a live Azure call. Closed months are served frozen.

import { jsonRequest as req } from './http'
import { swrInvalidate } from './swrCache'

/** Contribution before shared expenses: license − reserve − its own tenant DB. The split happens on the month. */
export interface FinanceCustomerRow {
  pharmacy_id: string
  name: string
  slug: string | null
  licensed: boolean
  monthly_license_usd: number | null
  reserve_usd: number | null
  /** null = no DB cost row for this month (shown as "—", never $0.00) */
  db_cost_usd: number | null
  contribution_usd: number | null
  contribution_pct: number | null
  /** billing was stopped for this customer (shown as ended, not as a $0 customer) */
  ended: boolean
  /** this customer's other income minus credits for the month */
  other_income_usd: number
}

export type FinanceBucket = 'customer' | 'unlicensed' | 'overhead'

export interface FinanceResourceRow {
  resource: string
  kind: string
  bucket: FinanceBucket
  pharmacy_id: string | null
  customer_name: string | null
  cost_usd: number
  prior_usd: number | null
}

/** Every real expense of the month. azure = the whole resource group (customer DBs + shared + unlicensed). */
export interface FinanceExpenses {
  customer_db_usd: number
  azure_shared_usd: number
  unlicensed_db_usd: number
  azure_usd: number
  /** 0 when GitHub reported no net cost; check detail.github_available for "not connected" */
  github_usd: number
  other_usd: number
  total_usd: number
}

/** Net profit = revenue − reserve − expenses; then split Blue Dev / Owl. */
export interface FinancePnl {
  revenue_usd: number
  reserve_usd: number
  expenses_usd: number
  net_profit_usd: number
  blue_dev_usd: number
  owl_usd: number
  net_margin_pct: number
  customers_licensed: number
  /** revenue = license_usd + other_income_usd − credits_usd */
  license_usd: number
  other_income_usd: number
  credits_usd: number
  /** the split in force for THIS month */
  reserve_pct: number
  dev_split_pct: number
}

export type FinanceMonthStatus = 'mtd' | 'open' | 'closed' | 'reopened'
export type FinanceCostSource = 'azure_export' | 'snapshot'

export interface FinanceMonthRow {
  month: string
  status: FinanceMonthStatus
  cost_source: FinanceCostSource
  captured_at: string
  revenue_usd: number
  reserve_usd: number
  azure_usd: number
  github_usd: number
  other_usd: number
  expenses_usd: number
  net_profit_usd: number
  blue_dev_usd: number
  owl_usd: number
  net_margin_pct: number
  license_usd: number
  other_income_usd: number
  credits_usd: number
}

export interface FinanceSummary {
  month: string
  current_month: string
  months: string[]
  cost_available: boolean
  cost_reason: string | null
  cost_as_of: string | null
  cost_stale: boolean
  cost_stale_after_hours: number
  cost_source: FinanceCostSource
  /** null = the month was never closed */
  close: {
    status: 'closed' | 'reopened'
    version: number
    closed_at: string | null
    closed_by: string | null
    reopened_at: string | null
    reopened_by: string | null
    reopen_reason: string | null
    frozen_at: string | null
  } | null
  auto_close_day: number
  auto_close_enabled: boolean
  settings: { reserve_pct: number; infra_pct: number; dev_split_pct: number }
  mrr_usd: number
  detail: {
    customers: FinanceCustomerRow[]
    resources: FinanceResourceRow[]
    expenses: FinanceExpenses
    pnl: FinancePnl
    other_lines: FinanceOtherLine[]
    /** other income and credits landing in the month */
    income_lines: FinanceOtherLine[]
    cost_source: FinanceCostSource
    github_available: boolean
    meters: { resource: string; meter: string; cost_usd: number }[]
    github_lines: { product: string; sku: string | null; repo: string | null; quantity: number | null; unit: string | null; net_usd: number }[]
    /** estimate: the month's Actions net cost shared by run wall time */
    github_workflows: { workflow: string; runs: number; minutes: number; est_usd: number }[]
  } | null
  history: FinanceMonthRow[]
}

// ── v2.2: manual / recurring overhead ("Other") ──────────────────────────────────────────────────
export type CostCadence = 'one_off' | 'monthly' | 'annual_spread'
/** cost = shared expense · income = adds to revenue · credit = a discount/refund that reduces revenue */
export type CostKind = 'cost' | 'income' | 'credit'

export interface CostItem {
  id: string
  title: string
  vendor: string | null
  category: string
  amount_usd: number
  cadence: CostCadence
  start_month: string        // YYYY-MM
  end_month: string | null   // YYYY-MM; null = continues every month
  reference: string | null
  notes: string | null
  kind: CostKind
  /** income/credit only — the customer it belongs to (null = not tied to one) */
  pharmacy_id: string | null
}

export type CostItemInput = Omit<CostItem, 'id'>

/** One cost as it lands in the selected month (overrides applied; a skipped month shows 0). */
export interface FinanceOtherLine {
  item_id: string
  title: string
  vendor: string | null
  category: string
  cadence: CostCadence
  base_usd: number
  amount_usd: number
  skipped: boolean
  overridden: boolean
  reason: string | null
  start_month: string
  end_month: string | null
  reference: string | null
  kind: CostKind
  pharmacy_id: string | null
  customer_name: string | null
}

export const listCostItems = () => req<{ items: CostItem[] }>('GET', '/ops/finance/items')

// Every /ops/* read is SWR-cached (api/swrCache.ts), so a write must drop the finance frames or the
// screen's post-save refetch is served the pre-write summary (same fix as CV-5's /views mutations).
const write = <T>(p: Promise<T>) => p.then(r => { swrInvalidate('/ops/finance'); return r })

export const createCostItem = (body: CostItemInput) => write(req<CostItem>('POST', '/ops/finance/items', body))
export const updateCostItem = (id: string, body: CostItemInput) => write(req<CostItem>('PUT', `/ops/finance/items/${id}`, body))
/** endMonth = last month it counts (YYYY-MM); null resumes it with no end. */
export const cutOffCostItem = (id: string, endMonth: string | null) =>
  write(req<CostItem>('POST', `/ops/finance/items/${id}/cut-off`, { end_month: endMonth }))
export const setCostItemMonth = (id: string, month: string, body: { skipped: boolean; amount_usd?: number | null; reason?: string | null }) =>
  write(req<{ ok: true }>('PUT', `/ops/finance/items/${id}/months/${month}`, body))
export const resetCostItemMonth = (id: string, month: string) =>
  write(req<{ ok: true }>('DELETE', `/ops/finance/items/${id}/months/${month}`))
export const removeCostItem = (id: string) => write(req<{ ok: true }>('DELETE', `/ops/finance/items/${id}`))

// ── v2.3 / v2.4: feeds and month close ───────────────────────────────────────────────────────────────
export type FinanceFeedSource = 'azure_export' | 'github'

export interface FinanceFeed {
  source: FinanceFeedSource
  label: string
  configured: boolean
  how_to: string
  last_success_at: string | null
  last_run: {
    status: 'running' | 'succeeded' | 'failed' | 'skipped'
    rows_written: number
    total_usd: number | null
    detail: string | null
    triggered_by: string
    started_at: string
    finished_at: string | null
  } | null
}

export interface FinanceSources {
  feeds: FinanceFeed[]
  snapshot: { label: string; last_captured_at: string | null }
}

export const getFinanceSources = () => req<FinanceSources>('GET', '/ops/finance/sources')
export const refreshFinanceSource = (source: FinanceFeedSource | 'close') =>
  write(req<{ status: string; detail?: string; closed?: string[] }>('POST', `/ops/finance/sources/${source}/refresh`))
export const closeFinanceMonth = (month: string) => write(req<{ ok: true; version: number }>('POST', `/ops/finance/months/${month}/close`))
export const reopenFinanceMonth = (month: string, reason: string) =>
  write(req<{ ok: true; recloses_after_hours: number }>('POST', `/ops/finance/months/${month}/reopen`, { reason }))

// ── customer pricing: schedule + change history ──────────────────────────────────────────────────────
export interface CustomerPricing {
  pharmacy_id: string
  name: string
  current_monthly_usd: number | null
  current_since: string | null
  /** billing is stopped right now */
  ended: boolean
  schedule: {
    effective_from: string; monthly_usd: number | null; active: boolean; notes: string | null; is_end: boolean
    state: 'current' | 'ended' | 'scheduled' | 'past' | 'inactive'
  }[]
  changes: {
    id: string
    effective_from: string
    old_monthly_usd: number | null
    /** null for a stop */
    new_monthly_usd: number | null
    change_type: 'price' | 'stop'
    reason: string
    source: 'profitability_v2' | 'current_tab' | 'existing'
    changed_by: string
    changed_at: string
  }[]
}

export const getCustomerPricing = (pharmacyId: string) => req<CustomerPricing>('GET', `/ops/finance/customers/${pharmacyId}/pricing`)
/** effectiveMonth = YYYY-MM; the price applies from the 1st of that month on. Earlier months keep their price. */
export const setCustomerPrice = (pharmacyId: string, monthlyLicenseUsd: number, effectiveMonth: string, reason: string) =>
  write(req<{ ok: true; old_monthly_usd: number | null; new_monthly_usd: number | null; effective_from: string }>(
    'PUT', `/ops/finance/customers/${pharmacyId}/pricing`, { monthly_license_usd: monthlyLicenseUsd, effective_month: effectiveMonth, reason }))
/** Stop billing from the 1st of effectiveMonth. Earlier months keep their price; a later price resumes billing. */
export const stopCustomerBilling = (pharmacyId: string, effectiveMonth: string, reason: string) =>
  write(req<{ ok: true; old_monthly_usd: number | null; effective_from: string }>(
    'PUT', `/ops/finance/customers/${pharmacyId}/pricing`, { stop_billing: true, effective_month: effectiveMonth, reason }))

// ── split percentages (from a month on, with history) + automatic month close switch ─────────────────
export interface FinanceSplitInfo {
  current: { reserve_pct: number; dev_split_pct: number; owl_pct: number }
  auto_close_enabled: boolean
  auto_close_day: number
  changes: {
    id: string; effective_from: string; reserve_pct: number; dev_split_pct: number; owl_pct: number
    reason: string; source: 'profitability_v2' | 'existing'; changed_by: string; changed_at: string
  }[]
}
export const getFinanceSplit = () => req<FinanceSplitInfo>('GET', '/ops/finance/split')
export const setFinanceSplit = (reservePct: number, devSplitPct: number, effectiveMonth: string, reason: string) =>
  write(req<{ ok: true }>('PUT', '/ops/finance/split', { reserve_pct: reservePct, dev_split_pct: devSplitPct, effective_month: effectiveMonth, reason }))
export const setAutoClose = (enabled: boolean) =>
  write(req<{ ok: true; auto_close_enabled: boolean }>('PUT', '/ops/finance/settings/auto-close', { enabled }))

type Detail = NonNullable<FinanceSummary['detail']>

/**
 * A closed month is served verbatim as frozen. A month frozen by an older build lacks the fields added since
 * (#2788: income_lines, pnl.license_usd / other_income_usd / credits_usd / reserve_pct / dev_split_pct,
 * customers[].ended / other_income_usd, line.kind). The server fills them on read (FinanceFrozenDetail.cs);
 * this fills them once more at the one place the summary enters the app, so no projection ever reads a hole
 * — Aug/Sep 2026 white-screened in production on exactly that. Present fields are never changed.
 */
export function normalizeFinanceSummary(s: FinanceSummary): FinanceSummary {
  const d = s.detail
  if (!d) return s
  const split = s.settings ?? { reserve_pct: 0, infra_pct: 0, dev_split_pct: 0 }
  // Older payloads may lack these; the type says they are there, so read through a partial view.
  const p: Partial<FinancePnl> = d.pnl ?? {}
  const e: Partial<FinanceExpenses> = d.expenses ?? {}
  const line = (kind: CostKind) => (l: Partial<FinanceOtherLine>): FinanceOtherLine =>
    ({ ...(l as FinanceOtherLine), kind: l.kind ?? kind, pharmacy_id: l.pharmacy_id ?? null, customer_name: l.customer_name ?? null })
  const detail: Detail = {
    ...d,
    cost_source: d.cost_source ?? 'snapshot',
    github_available: d.github_available ?? false,
    pnl: {
      revenue_usd: p.revenue_usd ?? 0, reserve_usd: p.reserve_usd ?? 0, expenses_usd: p.expenses_usd ?? 0, net_profit_usd: p.net_profit_usd ?? 0,
      blue_dev_usd: p.blue_dev_usd ?? 0, owl_usd: p.owl_usd ?? 0, net_margin_pct: p.net_margin_pct ?? 0, customers_licensed: p.customers_licensed ?? 0,
      // pre-#2788 revenue was licenses only; the split is the month's
      license_usd: p.license_usd ?? p.revenue_usd ?? 0, other_income_usd: p.other_income_usd ?? 0, credits_usd: p.credits_usd ?? 0,
      reserve_pct: p.reserve_pct ?? split.reserve_pct, dev_split_pct: p.dev_split_pct ?? split.dev_split_pct,
    },
    expenses: {
      customer_db_usd: e.customer_db_usd ?? 0, azure_shared_usd: e.azure_shared_usd ?? 0, unlicensed_db_usd: e.unlicensed_db_usd ?? 0,
      azure_usd: e.azure_usd ?? 0, github_usd: e.github_usd ?? 0, other_usd: e.other_usd ?? 0, total_usd: e.total_usd ?? 0,
    },
    customers: (d.customers ?? []).map((c: Partial<FinanceCustomerRow>) => ({ ...(c as FinanceCustomerRow), ended: c.ended ?? false, other_income_usd: c.other_income_usd ?? 0 })),
    resources: d.resources ?? [],
    other_lines: (d.other_lines ?? []).map(line('cost')),
    income_lines: (d.income_lines ?? []).map(line('income')),
    meters: d.meters ?? [],
    github_lines: d.github_lines ?? [],
    github_workflows: d.github_workflows ?? [],
  }
  return { ...s, detail }
}

export const getFinanceSummary = (month?: string) =>
  req<FinanceSummary>('GET', `/ops/finance/summary${month ? `?month=${encodeURIComponent(month)}` : ''}`).then(normalizeFinanceSummary)

export const financeKeys = {
  all: ['ops', 'finance'] as const,
  summary: (month: string | undefined) => ['ops', 'finance', 'summary', month ?? 'current'] as const,
  items: ['ops', 'finance', 'items'] as const,
  sources: ['ops', 'finance', 'sources'] as const,
  pricing: (pharmacyId: string) => ['ops', 'finance', 'pricing', pharmacyId] as const,
  split: ['ops', 'finance', 'split'] as const,
}
